import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { isMobile } from 'react-device-detect';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { format } from 'date-fns';
import moment from 'moment';
import classnames from 'classnames';

import ArrowIcon from 'src/assets/layout/navigation/arrow.svg';
import NavigationIcon from 'src/assets/hosLogs/hosEditLog/navigation.svg';

import { Button } from 'src/components/Button';
import { StatusButton } from 'src/components/StatusButton';
import {
  EXCEPTION_TYPE,
  EXCEPTION_STATUS,
  DUTY_TYPE,
  exceptionTypeByExceptionStatus,
} from 'src/constants/ELDConstants';
import { DUTY_STATUS_ERRORS } from '../constants';

import { getHosLog, getUserLocations } from '../store';
import {
  addPendingException,
  setIsAddingException,
  setUnsavedHOSExceptionsEditsPresent,
} from '../store/hosLogStore';

import './hosException.scss';
import { Exception, UserLocationType } from '../hosLog/types';
import { isEmpty, some } from 'lodash';
import { PendingException } from 'src/common/types';
import HosLogEditModal from 'src/components/HOSLogEditModal/HosLogEditModal';

interface Props {
  logType: string;
  logStatus: number;
  logTitle: string;
  location: string;
  notes: string;
  exceptions: Exception[];
  pendingExceptions: PendingException[];
  onClose: () => void;
  onSaveEdits: () => void;
  showUnsavedExceptionModal: boolean;
  discardExceptionEdits: () => void;
  hideExceptionEditWarnModal: () => void;
  destinationPage: string;
}

export const HosException: React.FC<Props> = ({
  logStatus,
  logTitle,
  logType,
  location,
  notes,
  exceptions,
  pendingExceptions,
  onClose,
  onSaveEdits,
  showUnsavedExceptionModal,
  discardExceptionEdits,
  hideExceptionEditWarnModal,
  destinationPage = '',
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const userLocationsArray = useSelector(getUserLocations);

  const slicedLocationsArray = useMemo(() => {
    return userLocationsArray.slice(userLocationsArray.length - 5);
  }, [userLocationsArray]);
  const reverseLocationsArray = slicedLocationsArray.reverse();

  const [statusView, setStatusView] = useState(logStatus);

  const [isLoading, setLoading] = useState(false);

  const routeMatch = useRouteMatch<{ date: string }>();
  const dateParam = routeMatch.params.date;

  const dateObj = useMemo(() => moment(dateParam).toDate(), [dateParam]);
  const logItemDate = useMemo(() => format(dateObj, 'EEE - MMM dd, yyyy'), [dateObj]);

  const [locationValue, setLocationValue] = useState(location);
  const [noteValue, setNoteValue] = useState(notes ? notes : '');

  const [errorMessage, setErrorMessage] = useState('');
  const [locationError, setLocationError] = useState(false);
  const [noteError, setNoteError] = useState(false);

  const [exceptionType, setExceptionType] = useState(EXCEPTION_TYPE.ADVERSE_DRIVING);

  const [exceptionValue] = useState(logType);

  const eventLogObject = useSelector(getHosLog);

  const currentTime = moment(new Date()).format('hh:mm:ss');
  const currentDate = moment.tz(
    `${eventLogObject.logDate} ${currentTime}`,
    `${eventLogObject.timeZone}`,
  );
  const exceptionTimestamp = currentDate.clone().tz(`${eventLogObject.timeZone}`);

  const isError = useMemo(() => {
    return locationError || noteError;
  }, [locationError, noteError]);

  const exceptionTypeAvailable = useCallback(
    (type: EXCEPTION_STATUS) =>
      !some(exceptions, exception => exception.exceptionType === type) &&
      !some(
        pendingExceptions,
        exception => exceptionTypeByExceptionStatus[type] === exception.exceptionType,
      ),
    [exceptions, pendingExceptions],
  );

  const isStatusADAvailable = useMemo(() => exceptionTypeAvailable(EXCEPTION_STATUS.AD), [
    exceptionTypeAvailable,
  ]);
  const isStatusSHAvailable = useMemo(() => exceptionTypeAvailable(EXCEPTION_STATUS.SH), [
    exceptionTypeAvailable,
  ]);

  useEffect(() => {
    if (statusView === EXCEPTION_STATUS.AD) {
      setExceptionType(EXCEPTION_TYPE.ADVERSE_DRIVING);
    } else if (statusView === EXCEPTION_STATUS.SH) {
      setExceptionType(EXCEPTION_TYPE.SIXTEEN_HOUR);
    }
  }, [statusView]);

  const changeView = useCallback(
    view => (): void => {
      setStatusView(view);
    },
    [],
  );

  const validateLocation = useMemo(
    () => (): void => {
      if (locationValue) {
        setLocationError(false);
      } else {
        setLocationError(true);
        setErrorMessage(DUTY_STATUS_ERRORS.LOCATION);
      }
    },
    [locationValue],
  );

  const validateNotes = useMemo(
    () => (): void => {
      if (noteValue) {
        setNoteError(false);
      } else {
        setNoteError(true);
        setErrorMessage(DUTY_STATUS_ERRORS.NOTES);
      }
    },
    [noteValue],
  );

  const changeNoteValue = useCallback((event: React.FormEvent<HTMLInputElement>) => {
    setNoteValue(event.currentTarget.value);
  }, []);

  const changeLocationValue = useCallback((event: React.FormEvent<HTMLInputElement>) => {
    setLocationValue(event.currentTarget.value);
  }, []);

  const insertExceptionHandler = () => {
    if (locationValue && noteValue) {
      setLoading(true);
      dispatch(
        addPendingException({
          employeeId: eventLogObject.employeeId,
          logDate: eventLogObject.logDate,
          exceptionTimestamp: exceptionTimestamp.format(),
          exceptionType: exceptionType,
          comment: noteValue,
          location: locationValue,
        }),
      );
      onSaveEdits();
      discardExceptionEdits();
      dispatch(setIsAddingException(false));
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }

    validateLocation();
    validateNotes();
  };

  const insertLocationValue = useCallback(
    (item: UserLocationType): (() => void) => (): void =>
      setLocationValue(`${item.city}, ${item.state}`),
    [],
  );

  const buttonBackStyle = classnames('button-back', {
    'button-back-mobile': isMobile,
  });

  const headerStyle = classnames('hos-exception__header', {
    'hos-exception__header-desktop': !isMobile,
  });

  const logSettingsStyle = classnames('hos-exception__log-settings', {
    'hos-exception__log-settings-mobile': isMobile,
  });

  const tabStatusStyle = classnames('hos-exception__tab-status', {
    'hos-exception__tab-status-desktop': !isMobile,
  });

  const exceptionStatusStyle = classnames('hos-exception__duty-status', {
    'hos-exception__duty-status-desktop': !isMobile,
  });

  const infoItemStyle = classnames('hos-exception__info-item', {
    'hos-exception__info-item-error': locationError,
  });

  const infoTitleStyle = classnames('hos-exception__info-title', {
    'hos-exception__info-title-error': locationError,
  });

  const noteItemStyle = classnames('hos-exception__info-item', {
    'hos-exception__info-item-error': noteError,
  });

  const noteTitleStyle = classnames('hos-exception__info-title', {
    'hos-exception__info-title-error': noteError,
  });

  const buttonConfirmExceptionsStyle = classnames('hos-exception__status-button', {
    'hos-exception__status-button__secondary': isLoading,
  });

  useEffect(() => {
    let unsavedExceptionEditsPresent = false;
    unsavedExceptionEditsPresent =
      logStatus != statusView || location != locationValue || notes != noteValue;

    dispatch(setUnsavedHOSExceptionsEditsPresent(unsavedExceptionEditsPresent));
  }, [statusView, locationValue, noteValue]);

  const renderLogType = (): JSX.Element | false => {
    return (
      <>
        {logType === exceptionValue && (
          <Button
            className={buttonConfirmExceptionsStyle}
            onClick={insertExceptionHandler}
            title={isLoading ? 'Inserting Exceptions...' : 'Confirm Exceptions'}
            disabled={logStatus === statusView || isLoading}
          />
        )}
      </>
    );
  };

  return (
    <>
      {showUnsavedExceptionModal && (
        <HosLogEditModal
          title="Leave Page"
          message="All changes will be lost. Do you want to proceed?"
          proceedButtonText="Discard Changes"
          proceedButtonFn={() => {
            discardExceptionEdits();
            destinationPage.length > 0 && history.push(destinationPage);
          }}
          undoButtonText="Cancel"
          undoButtonFn={hideExceptionEditWarnModal}
        />
      )}
      <div className="hos-exception">
        <div className={headerStyle}>
          <Button title="Back" onClick={onClose} icon={ArrowIcon} className={buttonBackStyle} />
          {logTitle}
        </div>

        <div className={tabStatusStyle}>
          {isError && (
            <div className="hos-exception__show-error">
              <div className="hos-exception__error-title">{errorMessage}</div>
            </div>
          )}
          {logType === exceptionValue && (
            <div className="hos-exception__exceptions-block">
              <div className="hos-exception__exceptions-title">For {logItemDate}</div>
              <div className="hos-exception__exceptions-subtitle">
                Exceptions will be applied to your next shift for today.
              </div>
            </div>
          )}
          <div className="hos-exception__tab-status-btn">
            {logType === exceptionValue && (
              <div className="hos-exception__exceptions">
                {isStatusADAvailable && (
                  <StatusButton
                    active={statusView === EXCEPTION_STATUS.AD}
                    exceptionStatus={statusView}
                    onClick={changeView(EXCEPTION_STATUS.AD)}
                    dutyType={DUTY_TYPE.AD}
                    dutySubtitle="Adverse Driving"
                  />
                )}
                {isStatusSHAvailable && (
                  <StatusButton
                    active={statusView === EXCEPTION_STATUS.SH}
                    exceptionStatus={statusView}
                    onClick={changeView(EXCEPTION_STATUS.SH)}
                    dutyType={DUTY_TYPE.SH}
                    dutySubtitle="16 Hour"
                  />
                )}
              </div>
            )}
          </div>
        </div>
        <div className={logSettingsStyle}>
          <div className={infoItemStyle}>
            <div className={infoTitleStyle}>Location*</div>
            <div className="hos-exception__info-subtitle">
              <input
                value={locationValue}
                className="hos-exception__info-input"
                onChange={changeLocationValue}
              />
              {isMobile && (
                <img
                  src={NavigationIcon}
                  alt="NavigationIcon"
                  className="hos-exception__navigation-icon"
                />
              )}
            </div>
          </div>
          {!isMobile && (
            <div className="hos-exception__location-subtitle">
              {!isEmpty(reverseLocationsArray) && 'Location noted in this log:'}
              {reverseLocationsArray.map(item => {
                return (
                  <div
                    key={item.speed}
                    className="hos-exception__location-item"
                    onClick={insertLocationValue(item)}
                  >
                    {item.city}, {item.state}
                  </div>
                );
              })}
            </div>
          )}
          <div className={noteItemStyle}>
            <div className={noteTitleStyle}>Notes*</div>
            <div className="hos-edit-log__info-subtitle">
              <input
                value={noteValue}
                className="hos-edit-log__info-input"
                onChange={changeNoteValue}
              />
            </div>
          </div>
        </div>
        <div className={exceptionStatusStyle}>{renderLogType()}</div>
      </div>
    </>
  );
};
